import React, { useState, useEffect, createContext, useContext } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Portal from '@mui/material/Portal';
import Slide from '@mui/material/Slide';
import { SlideProps } from '@mui/material/Slide';
import { notificationService, Notification, NotificationType } from '../../services/notificationService';

interface NotificationContextType {
  showNotification: (type: NotificationType, title: string, message: string) => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

interface NotificationProviderProps {
  children: React.ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 5,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const unsubscribe = notificationService.subscribe((notification) => {
      setNotifications(prev => {
        const updated = [notification, ...prev];
        // Keep only the most recent notifications
        return updated.slice(0, maxNotifications);
      });
    });

    const unsubscribeRemovals = notificationService.subscribeRemovals((id) => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    });

    return () => {
      unsubscribe();
      unsubscribeRemovals();
    };
  }, [maxNotifications]);

  const handleClose = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    notificationService.remove(id);
  };

  const showNotification = (type: NotificationType, title: string, message: string) => {
    switch (type) {
      case 'success':
        notificationService.success(title, message);
        break;
      case 'error':
        notificationService.error(title, message);
        break;
      case 'warning':
        notificationService.warning(title, message);
        break;
      case 'info':
        notificationService.info(title, message);
        break;
    }
  };

  const removeNotification = (id: string) => {
    handleClose(id);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, removeNotification }}>
      {children}
      <Portal>
        <Stack
          spacing={1}
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            maxWidth: 400,
          }}
        >
          {notifications.map((notification, index) => (
            <Snackbar
              key={notification.id}
              open={true}
              TransitionComponent={SlideTransition}
              sx={{
                position: 'relative',
                transform: 'none !important',
                marginBottom: index > 0 ? 1 : 0,
              }}
            >
              <Alert
                severity={notification.type}
                onClose={() => handleClose(notification.id)}
                sx={{ width: '100%', minWidth: 300 }}
                action={
                  notification.actions && notification.actions.length > 0 ? (
                    <Stack direction="row" spacing={1}>
                      {notification.actions.map((action, actionIndex) => (
                        <Button
                          key={actionIndex}
                          size="small"
                          variant={action.variant || 'text'}
                          onClick={() => {
                            action.action();
                            if (!notification.persistent) {
                              handleClose(notification.id);
                            }
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  ) : undefined
                }
              >
                <AlertTitle>{notification.title}</AlertTitle>
                {notification.message}
              </Alert>
            </Snackbar>
          ))}
        </Stack>
      </Portal>
    </NotificationContext.Provider>
  );
};